import { useState, useEffect, useCallback } from 'react';
import { fetchGoldPrice } from '../services/goldApi';

/**
 * Custom hook for fetching and managing gold price data.
 * Data is cached in Firestore per period (3 periods/day).
 * Auto-refreshes when the period changes.
 * 
 * @param {string} currency - ISO currency code
 * @returns {Object} { data, loading, error, refetch }
 */
export const useGoldPrice = (currency) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!currency) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchGoldPrice(currency);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => {
    fetchData();

    // Check every 15 minutes if the period has changed.
    // When a new period starts, Firestore won't have data for it
    // so fetchGoldPrice will automatically call the API once
    // and cache the result for the rest of the period.
    const interval = setInterval(() => {
      fetchData();
    }, 15 * 60 * 1000); // every 15 minutes

    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};
